// ===============================================
// 🎨 LAS RECETAS DE COLOR QUE EL TEMA DE MUI NO CUBRE
//
// Todo lo que se pinta en el panel sale de `theme.palette` (ver `App.js`). Lo
// que vive aquí son las tres o cuatro cosas que la paleta no sabe expresar por
// sí sola —un degradado, una banda, la rueda de colores de las filas— y que
// antes estaban escritas a mano en cada pantalla, con su hex dentro.
//
// La regla, para lo que venga después: **si es un color, o sale de `theme` o
// sale de aquí**. Un `#rrggbb` suelto en un componente es un color que no se
// entera del modo oscuro.
//
// ## EL DEGRADADO DE UNA CABECERA DESTACADA
//
// La portada del panel, la cabecera de la tabla de respuestas y los títulos de
// los diálogos grandes se pintan con un degradado y SIEMPRE llevan texto blanco
// encima. Estaba escrito a mano en cada sitio —el mismo
// `linear-gradient(135deg, #1976d2 0%, #1565c0 100%)` copiado cuatro veces— y
// por eso el modo oscuro no lo tocaba: una franja azul brillante en medio de
// una página oscura, idéntica a la del modo claro.
//
// Aquí está una sola vez y sabe en qué modo está el panel.
// ===============================================

/**
 * Los dos extremos del degradado, por paleta y por modo.
 *
 * ⚠️ Son colores fijos a propósito, y no `theme.palette.primary.main/dark`.
 * En modo oscuro el primary del panel es azul CLARO (#90caf9, ver `App.js`),
 * que es lo correcto para texto e iconos sobre fondo oscuro y justo lo contrario
 * de lo que hace falta aquí: encima de esta franja va texto blanco, así que el
 * fondo tiene que ser oscuro en los dos modos. Los del modo claro son los que ya
 * estaban escritos en las pantallas; los del oscuro se eligieron para que el
 * blanco quede por encima de 4,5:1 (#0d3c61 da 11,4:1 y #7a3e00, 8,3:1).
 */
const EXTREMOS = {
  primary: { light: ['#1976d2', '#1565c0'], dark: ['#0d3c61', '#08263f'] },
  warning: { light: ['#ed6c02', '#e65100'], dark: ['#7a3e00', '#5c2e00'] },
  success: { light: ['#2e7d32', '#1b5e20'], dark: ['#14512a', '#0d3319'] }
};

/**
 * La barra lateral es la otra superficie de color con texto blanco encima, pero
 * va en vertical y ocupa toda la altura, así que tiene su propio par.
 *
 * ⚠️ El extremo de arriba era **#1e88e5**, y sobre ese azul el blanco da
 * 3,7:1 —y 2,3:1 el texto secundario al 70%—, o sea que la barra no cumplía ni
 * en modo claro. Bajando un tono (#1565c0 → #0d47a1) el blanco sube a 5,7 y 8,6
 * sin dejar de ser el azul de siempre. En oscuro va como las cabeceras.
 */
const EXTREMOS_BARRA = {
  light: ['#1565c0', '#0d47a1'],
  dark: ['#0d3c61', '#08263f']
};

/**
 * Degradado de una cabecera destacada, listo para `background`.
 *
 * Se usa dentro de un `sx` con forma de función, que es como MUI entrega el
 * tema: `sx={(theme) => ({ background: degradadoCabecera(theme) })}`.
 *
 * @param {object} theme  el tema de MUI (de `sx`, `useTheme()` o `styled`)
 * @param {'primary'|'warning'} paleta  `warning` para lo que avisa (editar)
 */
export const degradadoCabecera = (theme, paleta = 'primary') => {
  const extremos = EXTREMOS[paleta] || EXTREMOS.primary;
  const [desde, hasta] = extremos[modoDe(theme)];

  return `linear-gradient(135deg, ${desde} 0%, ${hasta} 100%)`;
};

/**
 * Color liso de la banda que va PEGADA a una cabecera con degradado —hoy la
 * fila de filtros de la tabla de respuestas—.
 *
 * Es el extremo claro del mismo degradado, para que se lea como parte de la
 * cabecera y no como una franja de otro sitio. Tenía su propio azul fijo
 * (`rgba(25,118,210,0.85)`) y en modo oscuro se quedaba encendido debajo de una
 * cabecera que ya se había apagado.
 */
export const fondoFilaFiltros = (theme, paleta = 'primary') => {
  const extremos = EXTREMOS[paleta] || EXTREMOS.primary;

  return extremos[modoDe(theme)][0];
};

/**
 * Degradado de la barra lateral del panel, de arriba abajo. Ver [EXTREMOS_BARRA].
 */
export const degradadoBarraLateral = (theme) => {
  const [desde, hasta] = EXTREMOS_BARRA[modoDe(theme)];

  return `linear-gradient(180deg, ${desde} 0%, ${hasta} 100%)`;
};

/**
 * 🎨 La rueda de colores del borde izquierdo de cada fila de una tabla.
 *
 * Es decorativa —solo dice "esta fila no es la anterior"—, y por eso son cuatro
 * tonos de la propia paleta y no colores inventados: así cambian con el tema en
 * vez de quedarse en el mismo azul, verde, naranja y morado de siempre.
 *
 * La usaban `FormsList` y `FormResponses` con su propia copia de la lista.
 */
export const coloresDeFila = (theme) => [
  theme.palette.primary.main,
  theme.palette.success.main,
  theme.palette.warning.main,
  theme.palette.secondary.main
];

const modoDe = (theme) => (theme?.palette?.mode === 'dark' ? 'dark' : 'light');

export default degradadoCabecera;
